/**
 * @fileoverview Wrapper component that adds elegant hover animation to any card.
 * @module shared/components/ui/AnimatedCard
 */

import { forwardRef, HTMLAttributes, ReactNode } from 'react'
import { cn } from '@/shared/lib/utils'

interface AnimatedCardProps extends HTMLAttributes<HTMLDivElement> {
  children: ReactNode
  /** Use subtle variant for less aggressive animation */
  variant?: 'default' | 'subtle'
  /** Show floating particles on hover */
  showParticles?: boolean
}

/**
 * Wrapper component that adds elegant hover animation effects to any content.
 * Features gradient border, rotating glow, and optional floating particles.
 *
 * @example
 * ```tsx
 * <AnimatedCard>
 *   <Card>Your content</Card>
 * </AnimatedCard>
 *
 * // Subtle variant for dashboards
 * <AnimatedCard variant="subtle" showParticles>
 *   <MetricCard data={data} />
 * </AnimatedCard>
 * ```
 */
export const AnimatedCard = forwardRef<HTMLDivElement, AnimatedCardProps>(
  ({ children, className, variant = 'default', showParticles = false, ...props }, ref) => {
    return (
      <div
        ref={ref}
        className={cn(
          'animated-card',
          variant === 'subtle' && 'animated-card-subtle',
          className
        )}
        {...props}
      >
        <div className="animated-card-blur" />
        {showParticles && (
          <div className="animated-card-particles">
            <div className="animated-card-particle" />
            <div className="animated-card-particle" />
            <div className="animated-card-particle" />
            <div className="animated-card-particle" />
          </div>
        )}
        <div className="animated-card-content h-full">{children}</div>
      </div>
    )
  }
)

AnimatedCard.displayName = 'AnimatedCard'
